import {getTestProps} from '@benchling/js-test-utils';
import {Paper, Group, Stack, Text, Box, createStyles} from '@mantine/core';
import React, {ReactNode} from 'react';

import {AntibodyComponentType} from 'antibodies/AntibodyConstants';
import {useAntibodyFormatsQuery} from 'antibodies/hooks/useAntibodyFormatsQuery';
import AntibodyComponentSvg from 'antibodies/nested-data-creation/antibody-component-visualization/AntibodyComponentSvg';
import Antibody from 'antibodies/visualization/Antibody';
import {pluralize} from 'BenchlingUtil';
import ProjectFolderField from 'registry/ProjectFolderField';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import {GenericEvent} from 'util/Types';

const useStyles = createStyles((theme) => ({
  mainContainer: {
    display: 'flex',
    alignItems: 'stretch',
  },
  summaryBox: {
    margin: 0,
    width: '250px',
    minWidth: '250px',
  },
  summarySection: {
    borderTop: `1px solid ${theme.colors.gray[3]}`,
    paddingTop: theme.spacing.sm,
  },
  contentArea: {
    display: 'flex',
    gap: '32px',
    alignItems: 'flex-start',
    padding: theme.spacing.md,
    paddingLeft: '48px',
  },
  svgSquare: {
    width: '96px',
    flexShrink: 0,
  },
  antibodyContainer: {
    width: '96px',
    height: '96px',
    backgroundColor: theme.colors.gray[0],
    border: `1px solid ${theme.colors.gray[1]}`,
    borderRadius: theme.radius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.sm,
  },
}));

export interface FinalizePaneProps {
  folderId: string | null;
  numPairs: number;
  setProjectFolder: (folderId: string) => void;
  formatId: string;
}

function SvgSquare({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: ReactNode;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Box data-testid={`svgSquare-${label}`} className={classes.svgSquare}>
      {children}
      <Stack spacing={1} mt="xs">
        <Text size="md" weight="bold">
          {label}
        </Text>
        <Text size="xs" color="dimmed">
          {count} new {pluralize(count, 'entity')}
        </Text>
      </Stack>
    </Box>
  );
}

export default function FinalizePane({
  folderId,
  numPairs,
  setProjectFolder,
  formatId,
}: FinalizePaneProps): JSX.Element {
  const {classes} = useStyles();

  const antibodyFormats = useAntibodyFormatsQuery();
  const antibodyFormat = antibodyFormats.find(({id}) => id === formatId);

  // FIXME this calculation currently uses hardcoded numbers for Fab format
  const antibodyCount = numPairs;
  const aaChainCount = 2 * numPairs;
  const aaDomainCount = 2 * numPairs;
  const dnaDomainCount = 0;
  const totalEntities = antibodyCount + aaChainCount + aaDomainCount + dnaDomainCount;

  return (
    <Stack spacing="md">
      <Paper withBorder={true} radius="md" p={0} className={classes.mainContainer}>
        <Paper bg="gray.0" p="lg" radius="md 0 0 md" className={classes.summaryBox}>
          <Stack justify="space-between" style={{height: '100%'}}>
            <Stack spacing="sm">
              <Text size="lg" weight="bold">
                Summary
              </Text>
              <Text size="sm" color="dimmed">
                Part and translation links on components are autofilled before registration
              </Text>
            </Stack>
            <Group
              position="apart"
              className={classes.summarySection}
              {...getTestProps({component: 'SummaryBox', element: 'entitiesSection'})}
            >
              <Text size="sm" color="dimmed" weight="bold">
                Entities to create
              </Text>
              <Text size="md" weight="bold" color="dimmed">
                {totalEntities}
              </Text>
            </Group>
          </Stack>
        </Paper>

        <Box className={classes.contentArea}>
          <SvgSquare label="DNA domain" count={dnaDomainCount}>
            <AntibodyComponentSvg componentType={AntibodyComponentType.DNA_DOMAIN} size={96} />
          </SvgSquare>
          <SvgSquare label="AA domain" count={aaDomainCount}>
            <AntibodyComponentSvg componentType={AntibodyComponentType.AA_DOMAIN} size={96} />
          </SvgSquare>
          <SvgSquare label="AA chain" count={aaChainCount}>
            <AntibodyComponentSvg componentType={AntibodyComponentType.AA_CHAIN} size={96} />
          </SvgSquare>
          <SvgSquare label={antibodyFormat?.name || 'Loading...'} count={antibodyCount}>
            <div className={classes.antibodyContainer}>
              {antibodyFormat ? <Antibody format={antibodyFormat} /> : <CenteredHorizontalThrobber />}
            </div>
          </SvgSquare>
        </Box>
      </Paper>

      <Box w="50%">
        <ProjectFolderField
          folderId={folderId}
          labelStyle={LABEL_STYLES.LABEL}
          onChange={(e: GenericEvent) => {
            setProjectFolder(e.target.value);
          }}
          isRequired={true}
        />
      </Box>
    </Stack>
  );
}
